import React, { useEffect, useRef, useState } from "react";
import {
  Input,
  Typography,
  Space,
  Alert,
  Spin,
  Row,
  Col,
  Card,
  Empty,
  Tag,
  Segmented,
  Divider,
} from "antd";
import {
  SendOutlined,
  UserOutlined,
  RobotOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { Button } from "../../../components/Common";
import { useMutations } from "../../../hooks/useApiMutation";

const { Text } = Typography;

const MODE_CHAT = "chat";     // /api/v1/chat with kb_ak_ key
const MODE_ADMIN = "admin";   // legacy /ai/completion via admin auth

/**
 * Playground offers two modes:
 *   • Chat (realistic): calls POST /api/v1/chat with a chosen kb_ak_... key —
 *     exercises the full middleware including authentication and billing.
 *     Pick a test-environment key to avoid being charged.
 *   • Admin (diagnostics): calls /ai/completion directly; handy for quick smoke
 *     tests while drafting prompts, without issuing keys.
 */
const PlaygroundTab = ({ agent }) => {
  const { mutate } = useMutations();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;
  const [mode, setMode] = useState(MODE_CHAT);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [conversationId, setConversationId] = useState(null);

  // Autoscroll the transcript pane to the newest message whenever the list or
  // loading indicator changes. A dedicated sentinel div at the bottom is cheaper
  // than computing scrollHeight on every render.
  const scrollAnchorRef = useRef(null);
  useEffect(() => {
    scrollAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [transcript, loading]);

  const resetConversation = () => {
    setTranscript([]);
    setConversationId(null);
  };

  const sendViaAdmin = async (text) => {
    const { data } = await mutateRef.current("playgroundAdminCall", {
      method: "post",
      endpoint: "/ai/completion",
      payload: {
        prompt: text,
        agentType: agent.name,
        // ExtractAIType enum: OpenAi = 1, ClaudeAi = 3.
        extractAIType: agent.provider === "Claude" ? 3 : 1,
      },
    });
    return data?.data;
  };

  /**
   * Admin-authenticated playground call. Routes through the agent's real
   * provider pipeline (tools, prompt, model) without needing a kb_ak_ key —
   * the backend flags the resulting TokenUsages row as IsTest=1.
   */
  const sendViaChat = async (text) => {
    const { data } = await mutateRef.current("playgroundChatCall", {
      method: "post",
      endpoint: `/v1/agents/${agent.agentId}/test`,
      payload: { prompt: text, conversationId },
    });
    return data?.data;
  };

  const send = async () => {
    const text = prompt.trim();
    if (!text) return;
    setTranscript((t) => [...t, { role: "user", text }]);
    setPrompt("");
    setLoading(true);

    try {
      const response = mode === MODE_CHAT
        ? await sendViaChat(text)
        : await sendViaAdmin(text);

      if (response?.conversationId) setConversationId(response.conversationId);

      setTranscript((t) => [
        ...t,
        {
          role: "agent",
          text: response?.content || response?.message || "(no content)",
          tokensUsed: response?.tokensUsed || 0,
          finishReason: response?.finishReason,
          toolCalls: response?.toolCalls,
        },
      ]);
    } catch (err) {
      setTranscript((t) => [
        ...t,
        {
          role: "error",
          text:
            err?.response?.data?.message ||
            err?.message ||
            "Request failed",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      send();
    }
  };

  return (
    <Row gutter={[24, 16]}>
      <Col xs={24} lg={16}>
        <Card
          size="small"
          title={
            <Space>
              <ThunderboltOutlined />
              Playground
              {mode === MODE_CHAT ? (
                <Tag color="blue">Real /api/v1/chat</Tag>
              ) : (
                <Tag color="gold">Admin test</Tag>
              )}
            </Space>
          }
          extra={
            <Segmented
              value={mode}
              onChange={(m) => {
                setMode(m);
                resetConversation();
              }}
              options={[
                { label: "Chat", value: MODE_CHAT },
                { label: "Admin", value: MODE_ADMIN },
              ]}
              size="small"
            />
          }
          styles={{ body: { padding: 0 } }}
        >

          <div
            style={{
              height: 400,
              overflowY: "auto",
              padding: 16,
              background: "rgba(0,0,0,0.02)",
            }}
          >
            {transcript.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  mode === MODE_CHAT
                    ? "Send a prompt through /api/v1/chat"
                    : "Send a prompt via admin path"
                }
              />
            ) : (
              transcript.map((m, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    marginBottom: 12,
                    justifyContent:
                      m.role === "user" ? "flex-end" : "flex-start",
                  }}
                >
                  <div
                    style={{
                      maxWidth: "80%",
                      background:
                        m.role === "user"
                          ? "var(--accent-blue)"
                          : m.role === "error"
                          ? "color-mix(in srgb, var(--accent-rose) 10%, var(--bg-surface))"
                          : "var(--bg-surface)",
                      color: m.role === "user" ? "#fff" : "inherit",
                      padding: 12,
                      borderRadius: 8,
                      border:
                        m.role === "error"
                          ? "1px solid var(--accent-rose)"
                          : "1px solid var(--border-subtle)",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    <Space size={6} style={{ marginBottom: 4 }}>
                      {m.role === "user" ? <UserOutlined /> : <RobotOutlined />}
                      <Text
                        strong
                        style={{
                          color: m.role === "user" ? "#fff" : "inherit",
                          fontSize: 12,
                        }}
                      >
                        {m.role === "user"
                          ? "You"
                          : m.role === "error"
                          ? "Error"
                          : agent.name}
                      </Text>
                      {m.tokensUsed > 0 && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {m.tokensUsed} tokens
                        </Text>
                      )}
                      {m.finishReason && (
                        <Tag color="default" style={{ fontSize: 10, lineHeight: "16px" }}>
                          {m.finishReason}
                        </Tag>
                      )}
                    </Space>
                    <div>{m.text}</div>
                    {m.toolCalls?.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <Divider style={{ margin: "8px 0" }} />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {m.toolCalls.length} tool call(s) requested:
                        </Text>
                        {m.toolCalls.map((tc, idx) => (
                          <div
                            key={idx}
                            style={{
                              fontFamily:
                                "ui-monospace, SFMono-Regular, Menlo, monospace",
                              fontSize: 11,
                              background: "rgba(0,0,0,0.04)",
                              padding: 6,
                              borderRadius: 4,
                              marginTop: 4,
                            }}
                          >
                            <strong>{tc.name}</strong>({tc.argumentsJson})
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
            {loading && (
              <div style={{ textAlign: "center", padding: 12 }}>
                <Spin />
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Agent is thinking…
                  </Text>
                </div>
              </div>
            )}
            {/* Sentinel: scrollIntoView target so newest turn is always visible. */}
            <div ref={scrollAnchorRef} />
          </div>

          <div
            style={{
              padding: 12,
              borderTop: "1px solid var(--border-subtle)",
              background: "var(--bg-surface)",
            }}
          >
            <Space.Compact style={{ width: "100%" }}>
              <Input.TextArea
                rows={2}
                placeholder="Type a prompt… (⌘/Ctrl + Enter to send)"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={onKey}
                disabled={loading}
              />
              <Button
                variant="primary"
                icon={<SendOutlined />}
                loading={loading}
                onClick={send}
                disabled={!prompt.trim()}
              >
                Send
              </Button>
            </Space.Compact>
            {conversationId && mode === MODE_CHAT && (
              <Space style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Conversation: <Text code>{conversationId}</Text>
                </Text>
                <Button
                  variant="ghost"
                  size="small"
                  onClick={resetConversation}
                >
                  Start new
                </Button>
              </Space>
            )}
          </div>
        </Card>
      </Col>

      <Col xs={24} lg={8}>
        <Card size="small" title="Active configuration">
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Row>
              <Col span={10}><Text type="secondary">Provider</Text></Col>
              <Col span={14}><Tag>{agent.provider}</Tag></Col>
            </Row>
            <Row>
              <Col span={10}><Text type="secondary">Model</Text></Col>
              <Col span={14}>
                {agent.model ? <Text code>{agent.model}</Text> : "—"}
              </Col>
            </Row>
            <Row>
              <Col span={10}><Text type="secondary">Temperature</Text></Col>
              <Col span={14}>{agent.temperature ?? "default"}</Col>
            </Row>
            <Row>
              <Col span={10}><Text type="secondary">Max tokens</Text></Col>
              <Col span={14}>{agent.maxTokens ?? "default"}</Col>
            </Row>
            <Row>
              <Col span={10}><Text type="secondary">Files</Text></Col>
              <Col span={14}>{agent.files?.length || 0}</Col>
            </Row>
            <Row>
              <Col span={10}><Text type="secondary">Tools</Text></Col>
              <Col span={14}>{agent.tools?.length || 0}</Col>
            </Row>
          </Space>

          {agent.status !== "Published" && mode === MODE_CHAT && (
            <Alert
              style={{ marginTop: 12 }}
              type="warning"
              showIcon
              message="Agent must be Published before kb_ak_ keys are accepted."
            />
          )}

          {mode === MODE_ADMIN && (
            <Alert
              style={{ marginTop: 12 }}
              type="info"
              showIcon
              message="Admin mode bypasses /api/v1/chat and calls the agent directly. Useful when drafting prompts."
            />
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default PlaygroundTab;
